// Client API de la section « Recettes & achats » (/api/compta, 27/10/2026).

import { api } from './api'
import type { LigneAchat, LigneRecette } from '@shared/calculs/registres'
import type { FraisCreateInput, ReleveTerminal, Terminal } from '@shared/schemas/compta'

export type { LigneAchat, LigneRecette }

const q = (debut: string, fin: string) =>
  `debut=${encodeURIComponent(debut)}&fin=${encodeURIComponent(fin)}`

export async function fetchRecettes(debut: string, fin: string): Promise<LigneRecette[]> {
  const { lignes } = await api<{ lignes: LigneRecette[] }>(`/api/compta/recettes?${q(debut, fin)}`)
  return lignes
}

export async function fetchAchats(debut: string, fin: string): Promise<LigneAchat[]> {
  const { lignes } = await api<{ lignes: LigneAchat[] }>(`/api/compta/achats?${q(debut, fin)}`)
  return lignes
}

// Saisie d'un frais : forme envoyée au serveur (validée par fraisCreateSchema).
export type FraisPayload = FraisCreateInput

export async function creerFrais(payload: FraisPayload): Promise<LigneAchat> {
  const { ligne } = await api<{ ligne: LigneAchat }>('/api/compta/frais', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return ligne
}

export async function majFrais(id: string, payload: FraisPayload): Promise<LigneAchat> {
  const { ligne } = await api<{ ligne: LigneAchat }>(`/api/compta/frais/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
  return ligne
}

export async function supprimerFrais(id: string): Promise<void> {
  await api(`/api/compta/frais/${id}`, { method: 'DELETE' })
}

// --- Import des relevés de terminal (27/10/2026) -------------------------------------
export interface ImportTerminal {
  id: string
  terminal: Terminal
  periodeDebut: string
  periodeFin: string
  nomFichier: string | null
  totalVentes: number
  createdAt: string
}

export async function fetchImports(): Promise<ImportTerminal[]> {
  const { imports } = await api<{ imports: ImportTerminal[] }>('/api/compta/imports')
  return imports
}

export async function importerReleve(releve: ReleveTerminal): Promise<ImportTerminal> {
  const r = await api<{ import: ImportTerminal }>('/api/compta/imports', {
    method: 'POST',
    body: JSON.stringify(releve),
  })
  return r.import
}

export async function annulerImport(id: string): Promise<void> {
  await api(`/api/compta/imports/${id}`, { method: 'DELETE' })
}

/** Recettes importées (date + part HT), pour le chiffre d'affaires du tableau de bord. */
export async function fetchEncaissementsCa(): Promise<Array<{ date: string; montantHt: number }>> {
  const { encaissements } = await api<{ encaissements: Array<{ date: string; montantHt: number }> }>(
    '/api/compta/encaissements-ca',
  )
  return encaissements
}
